import { NgClass } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    HostListener,
    inject,
    Input,
    input,
    output,
    signal,
    ViewChild,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
    BreakpointService,
    IconButtonComponent,
    Palette,
    SeparatorComponent,
    Size,
} from '@interacta-shared/ui';
import { uuid } from '@interacta-shared/util';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    selector: 'interacta-advanced-search-bar',
    templateUrl: './advanced-search-bar.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        NgClass,
        TranslatePipe,
        IconButtonComponent,
        MatTooltipModule,
        SeparatorComponent,
        ReactiveFormsModule,
    ],
})
export class AdvancedSearchBarComponent {
    private readonly breakpointService = inject(BreakpointService);

    @Input({ required: true }) search!: FormControl<string | null>;
    readonly disabled = input(false);
    readonly placeholder = input('');
    readonly bgColor =
        input<Extract<Palette, 'surface-A' | 'surface-B'>>('surface-A');
    readonly size = input<Extract<Size, 'regular' | 'small'>>('regular');
    readonly openLeft = input(false);
    readonly closedInputSize = input<'172' | '250' | '420'>('250');

    inputFocused = output();

    @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;

    isInputFocused = signal<boolean>(false);

    readonly isMd = computed(() => {
        return this.breakpointService.breakpointsSignal()?.isMd;
    });
    readonly isLg = computed(() => {
        return this.breakpointService.breakpointsSignal()?.isLg;
    });
    readonly componentId = `interacta-${uuid()}`;

    @HostListener('document:click', ['$event'])
    onClick(event: MouseEvent): void {
        this.checkTargetAndClose(event);
    }

    @HostListener('document:keydown.escape')
    onEscapeDown(): void {
        this.closeSearch();
    }

    openSearch(inputFocus = false): void {
        this.isInputFocused.set(true);
        if (inputFocus) this.searchInput?.nativeElement.focus();
        this.inputFocused.emit();
    }

    isComponentChild(element: HTMLElement | null): boolean {
        return !!element?.closest(`#${this.componentId}`);
    }

    /**
     * Checks if the next focus or click element is not a child component, if the condition is met closes the search.
     * When FocusEvent checks relatedTarget (the element focus destination), when PointerEvent checks click target.
     * @param event - The focus event to check the target against.
     */
    checkTargetAndClose(event: FocusEvent | PointerEvent): void {
        const nextFocusElem = (event.relatedTarget ??
            event.target) as HTMLElement;
        const isTabKey = nextFocusElem && nextFocusElem !== document.body;
        if (isTabKey && !this.isComponentChild(nextFocusElem)) {
            this.closeSearch();
        }
    }

    closeSearch(): void {
        this.isInputFocused.set(false);
    }
}
